import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Film, Download, Clock, HardDrive, CheckSquare, Square, X,
  AlertTriangle, Power, RefreshCw, XCircle, Play, Scissors, Layers, Globe, Wifi
} from 'lucide-react';
import api from '../services/api';

// Tiempo en formato del equipo (YYYYMMDDTHHMMSSZ) <-> segundos epoch (UTC).
const parseRaw = (raw) => {
  const m = (raw || '').match(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/);
  if (!m) return 0;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) / 1000;
};
const fmtRaw = (sec) => {
  const d = new Date(sec * 1000), p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`;
};
const mmss = (s) => {
  s = Math.max(0, Math.round(s));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const fmtBytes = (b) => {
  if (!b) return '0 MB';
  const mb = b / 1024 / 1024;
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(0)} MB`;
};
const fmtDur = (s) => {
  if (s == null) return '—';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return h ? `${h}h ${m}m` : m ? `${m}m ${ss}s` : `${ss}s`;
};
const fmtClock = (iso) => {
  const m = (iso || '').match(/T(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : iso;
};
const fmtDate = (iso) => {
  const m = (iso || '').match(/(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Recordings = () => {
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [channel, setChannel] = useState(1);
  const [date, setDate] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [segments, setSegments] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [queue, setQueue] = useState({ jobs: [], keep_on_seconds: 0, remaining_bytes: 0, pending_count: 0 });
  const [player, setPlayer] = useState(null);   // {src, title, seg?} para el reproductor
  const [mode, setMode] = useState(null);       // {mode:'rtsp'|'isup', clean:bool}

  useEffect(() => {
    api.get('/api/devices').then(r => {
      const list = (r.data || []);
      setDevices(list);
      const on = list.find(d => d.is_online) || list[0];
      if (on) setDeviceId(on.device_id);
    });
  }, []);

  const device = useMemo(() => devices.find(d => d.device_id === deviceId), [devices, deviceId]);
  const channels = useMemo(() => {
    if (!device) return [1];
    if (device.active_channels) {
      const p = device.active_channels.split(',').map(s => parseInt(s.trim(), 10)).filter(Boolean);
      if (p.length) return p;
    }
    return Array.from({ length: device.channels || 1 }, (_, i) => i + 1);
  }, [device]);

  const loadSummary = useCallback(() => {
    if (!deviceId) return;
    api.get(`/api/devices/${deviceId}/recordings/summary?channel=${channel}`)
      .then(r => setSummary(r.data)).catch(() => setSummary(null));
  }, [deviceId, channel]);

  const loadSegments = useCallback(() => {
    if (!deviceId) return;
    setLoading(true); setErr(null); setSel(new Set());
    api.get(`/api/devices/${deviceId}/recordings?channel=${channel}&date=${date}`)
      .then(r => setSegments(r.data.segments || []))
      .catch(e => { setSegments([]); setErr(e.response?.data?.detail || 'No se pudo consultar el equipo (¿alcanzable?)'); })
      .finally(() => setLoading(false));
  }, [deviceId, channel, date]);

  useEffect(() => { loadSummary(); loadSegments(); }, [loadSummary, loadSegments]);

  // Modo de reproducción del equipo (LAN limpio por RTSP vs remoto por ISUP)
  useEffect(() => {
    if (!deviceId) { setMode(null); return; }
    let alive = true;
    api.get(`/api/devices/${deviceId}/playback-mode`)
      .then(r => { if (alive) setMode(r.data); })
      .catch(() => { if (alive) setMode(null); });
    return () => { alive = false; };
  }, [deviceId]);

  // Poll de la cola de descargas
  useEffect(() => {
    let alive = true;
    const f = () => api.get('/api/downloads').then(r => { if (alive) setQueue(r.data); }).catch(() => {});
    f();
    const id = setInterval(f, 2000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const toggle = (i) => setSel(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const allSel = segments.length > 0 && sel.size === segments.length;
  const toggleAll = () => setSel(allSel ? new Set() : new Set(segments.map((_, i) => i)));

  const selectedBytes = useMemo(
    () => [...sel].reduce((a, i) => a + (segments[i]?.size || 0), 0), [sel, segments]);

  const enqueue = (segs) => {
    Promise.all(segs.map(seg => {
      // Sin playback_uri (p.ej. dashcams listadas por SDK): no hay descarga
      // directa por ISAPI -> bajar por TRAMOS (RTSP-playback limpio).
      if (!seg.playback_uri) {
        const stamp = fmtClock(seg.start).replace(/:/g, '');
        return api.post('/api/downloads/chunked', {
          device_id: deviceId, channel, start: seg.start_raw, end: seg.end_raw,
          chunk_seconds: 120, filename: `tramos_ch${channel}_${stamp}.mp4`,
        });
      }
      return api.post('/api/downloads', {
        device_id: deviceId, channel, start: seg.start, end: seg.end,
        size: seg.size, filename: seg.filename, playback_uri: seg.playback_uri,
      });
    })).then(() => api.get('/api/downloads').then(r => setQueue(r.data)));
  };
  const enqueueSelected = () => { enqueue([...sel].map(i => segments[i])); setSel(new Set()); };

  const cancel = (id) => api.post(`/api/downloads/${id}/cancel`).then(() => api.get('/api/downloads').then(r => setQueue(r.data)));
  const clearDone = () => api.post('/api/downloads/clear').then(() => api.get('/api/downloads').then(r => setQueue(r.data)));

  const saveFile = (job) => {
    api.get(`/api/downloads/${job.id}/file`, { responseType: 'blob' }).then(r => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = job.filename || `video_${job.id}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    });
  };

  const token = localStorage.getItem('token') || '';
  // Reproducción INTELIGENTE: el backend elige RTSP (LAN, limpio) o ISUP (remoto).
  const playSegment = (seg) => setPlayer({
    seg, isup: mode && mode.mode === 'isup',
    title: `Cam ${channel} · ${fmtClock(seg.start)} → ${fmtClock(seg.end)}`,
    src: `/api/devices/${deviceId}/playback-auto.mp4?channel=${channel}` +
         `&start=${seg.start_raw}&end=${seg.end_raw}&token=${encodeURIComponent(token)}`,
  });
  const playJob = (job) => setPlayer({
    title: job.filename,
    src: `/api/downloads/${job.id}/mp4?token=${encodeURIComponent(token)}`,
  });

  const enqueueChunked = (seg) => {
    const stamp = fmtClock(seg.start).replace(/:/g, '');
    api.post('/api/downloads/chunked', {
      device_id: deviceId, channel, start: seg.start_raw, end: seg.end_raw,
      chunk_seconds: 120, filename: `tramos_ch${channel}_${stamp}.mp4`,
    }).then(() => api.get('/api/downloads').then(r => setQueue(r.data)));
  };

  // Descarga por túnel ISUP: la única vía cuando no hay IP local. Sale
  // comprimida. Si además el equipo va por Samsara o datos móviles, se avisa
  // del consumo y hay que confirmar: ante un siniestro interesa el video YA,
  // pero si no urge lo sensato es encolarlo y que baje al llegar a WiFi.
  const enqueueIsup = (seg) => {
    const porDatos = mode && mode.consume_datos;
    const aviso = porDatos
      ? `${mode.aviso}\n\nAdemás sale comprimida (el MDVR empuja el playback con pérdida).\n\n¿Descargar ahora de todas formas?`
      : 'Descarga por túnel ISUP.\n\nSale comprimida (el MDVR empuja el playback con pérdida) y puede traer menos rango del pedido. Para copia fiel, usa la descarga por tramos con el equipo en LAN/VPN.\n\n¿Continuar?';
    if (!window.confirm(aviso)) return;
    const stamp = fmtClock(seg.start).replace(/:/g, '');
    api.post('/api/downloads/isup', {
      device_id: deviceId, channel, start: seg.start_raw, end: seg.end_raw,
      filename: `isup_ch${channel}_${stamp}.mp4`,
      forzar_datos: !!porDatos,
    }).then(() => api.get('/api/downloads').then(r => setQueue(r.data)))
      .catch(e => setErr(e.response?.data?.detail || 'No se pudo encolar'));
  };

  const enqueueClip = (seg, fromS, toS) => {
    const base = parseRaw(seg.start_raw);
    const start = fmtRaw(base + fromS), end = fmtRaw(base + toS);
    const stamp = fmtClock(seg.start).replace(/:/g, '') + `_${Math.round(fromS)}-${Math.round(toS)}`;
    api.post('/api/clips', {
      device_id: deviceId, channel, start, end,
      duration: Math.max(1, Math.round(toS - fromS)),
      filename: `recorte_ch${channel}_${stamp}.mp4`,
    }).then(() => api.get('/api/downloads').then(r => setQueue(r.data)));
  };

  const keepOn = queue.keep_on_seconds || 0;
  const reachable = device && device.is_online;   // el 502 al listar ya avisa si no

  return (
    <div style={s.page} className="rec">
      <style>{CSS}</style>
      <div style={s.header}>
        <div style={s.title}><div style={s.titleIcon}><Film size={20} /></div>
          <div><div style={s.h1}>Grabaciones</div><div style={s.h1sub}>Reproduce, recorta y descarga · calidad automática</div></div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {mode && (
            <span style={{ ...s.modePill, ...(mode.clean ? s.modeClean : s.modeIsup) }}
                  title={mode.clean
                    ? 'El equipo es alcanzable: reproduce por RTSP, video limpio 1080p'
                    : 'Equipo remoto: reproduce por túnel ISUP (comprimido por el MDVR)'}>
              {mode.clean ? <Wifi size={13} /> : <Globe size={13} />}
              {mode.clean ? 'LAN · limpio' : 'Remoto · ISUP'}
            </span>
          )}
          {device && (
            <span style={{ ...s.netPill, ...(device.is_online ? s.netOn : s.netOff) }}>
              {device.local_ip ? `IP ${device.local_ip}` : (device.is_online ? 'en línea' : 'offline')}
            </span>
          )}
        </div>
      </div>

      {/* Controles */}
      <div style={s.controls}>
        <select value={deviceId} onChange={e => setDeviceId(e.target.value)} style={s.select}>
          {devices.map(d => <option key={d.device_id} value={d.device_id}>{d.name || d.device_id}</option>)}
        </select>
        <div style={s.chips}>
          {channels.map(c => <button key={c} onClick={() => setChannel(c)} style={c === channel ? s.chipA : s.chip}>Cam {c}</button>)}
        </div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={s.date} />
        <button onClick={loadSegments} style={s.reload}><RefreshCw size={15} /> Actualizar</button>
      </div>

      {/* Resumen: desde cuándo hay grabación */}
      <div style={s.summary}>
        {summary && summary.has_recordings ? (
          <>
            <div style={s.sumItem}><Clock size={16} color="#2563eb" /><div>
              <div style={s.sumLabel}>Grabación desde</div>
              <div style={s.sumVal}>{fmtDate(summary.oldest)} {fmtClock(summary.oldest)}</div>
            </div></div>
            <div style={s.sumItem}><Film size={16} color="#2563eb" /><div>
              <div style={s.sumLabel}>Hasta</div><div style={s.sumVal}>{fmtDate(summary.newest)} {fmtClock(summary.newest)}</div>
            </div></div>
            <div style={s.sumItem}><HardDrive size={16} color="#2563eb" /><div>
              <div style={s.sumLabel}>Total en SD (ch{summary.channel})</div>
              <div style={s.sumVal}>{fmtBytes(summary.total_bytes)} · {fmtDur(summary.total_seconds)}</div>
            </div></div>
            {summary.via === 'isup' && (
              <div style={s.sumItem}><Globe size={16} color="#b45309" /><div>
                <div style={s.sumLabel}>Consulta</div>
                <div style={{ ...s.sumVal, color: '#b45309' }}>vía túnel ISUP</div>
              </div></div>
            )}
          </>
        ) : (
          <div style={s.sumEmpty}>{summary ? 'Sin grabaciones en este canal.' : 'Consultando…'}</div>
        )}
      </div>

      <div style={s.body}>
        {/* Segmentos */}
        <div style={s.col}>
          <div style={s.colHead}>
            <span>Segmentos del {date} · Cam {channel}</span>
            {segments.length > 0 && (
              <button style={s.linkBtn} onClick={toggleAll}>{allSel ? 'Ninguno' : 'Todos'}</button>
            )}
          </div>
          {mode && mode.consume_datos && (
            <div style={s.avisoDatos}>
              <AlertTriangle size={15} />
              <span>
                Conectado por <b>{mode.connection_type === 'samsara' ? 'Samsara' : 'datos móviles'}</b>.
                Descargar ahora gasta del plan; el botón te lo confirmará.
                Si no urge, usa <b>Descargar por tramos</b>: se encola y baja sola al llegar a WiFi.
              </span>
            </div>
          )}
          {err && <div style={s.err}><AlertTriangle size={15} /> {err}</div>}
          {loading ? <div style={s.muted}>Cargando…</div> : (
            <div style={s.segList}>
              {segments.map((seg, i) => (
                <div key={i} data-row style={sel.has(i) ? s.segRowA : s.segRow} onClick={() => toggle(i)}>
                  {sel.has(i) ? <CheckSquare size={17} color="#3b82f6" /> : <Square size={17} color="#94a3b8" />}
                  <div style={{ flex: 1 }}>
                    <div style={s.segTime}>{fmtClock(seg.start)} → {fmtClock(seg.end)}</div>
                    <div style={s.segMeta}>{fmtDur(seg.duration_seconds)} · {fmtBytes(seg.size)}</div>
                  </div>
                  <button data-act style={s.playBtn} onClick={(e) => { e.stopPropagation(); playSegment(seg); }} title="Reproducir / recortar (elige RTSP limpio o ISUP según alcance)">
                    <Play size={15} />
                  </button>
                  <button data-act style={s.chunkBtn}
                          onClick={(e) => { e.stopPropagation(); enqueueChunked(seg); }}
                          title={mode && mode.mode === 'isup'
                            ? 'Encolar limpio: baja SOLO al llegar la unidad a la sucursal (WiFi/LAN)'
                            : 'Descargar por tramos (limpio, resiliente)'}>
                    <Layers size={15} />
                  </button>
                  {mode && mode.mode === 'isup' ? (
                    <button data-act style={s.isupDlBtn} onClick={(e) => { e.stopPropagation(); enqueueIsup(seg); }}
                            title={mode && mode.consume_datos
                              ? 'Descargar YA por el túnel (comprimido). Gasta datos móviles: pedirá confirmación'
                              : 'Descargar por el túnel (comprimido). No hace falta IP local'}>
                      <Globe size={15} />
                    </button>
                  ) : seg.playback_uri ? (
                    <button data-act style={s.dlBtn} onClick={(e) => { e.stopPropagation(); enqueue([seg]); }} title="Descargar archivo completo (rápido, pero se reinicia si se corta)">
                      <Download size={15} />
                    </button>
                  ) : null}
                </div>
              ))}
              {!segments.length && !err && <div style={s.muted}>Sin segmentos ese día.</div>}
            </div>
          )}
          {sel.size > 0 && (
            <button style={s.enqueueBtn} onClick={enqueueSelected}>
              <Download size={16} /> Encolar {sel.size} ({fmtBytes(selectedBytes)})
            </button>
          )}
        </div>

        {/* Cola de descargas */}
        <div style={s.col}>
          <div style={s.colHead}>
            <span>Cola de descargas</span>
            {queue.jobs?.length > 0 && <button style={s.linkBtn} onClick={clearDone}>Limpiar terminadas</button>}
          </div>

          {/* Aviso: mantén el MDVR prendido */}
          {keepOn > 0 && (
            <div style={s.keepCard}>
              <Power size={20} color="#b45309" />
              <div>
                <div style={s.keepTitle}>Mantén el MDVR encendido ~{fmtDur(keepOn)}</div>
                <div style={s.keepSub}>
                  Faltan {fmtBytes(queue.remaining_bytes)} · {queue.pending_count} en cola.
                  Si el equipo se apaga, la descarga en curso se cancela.
                </div>
              </div>
            </div>
          )}

          <div style={s.jobList}>
            {(queue.jobs || []).map(j => (
              <div key={j.id} style={s.job}>
                <div style={s.jobTop}>
                  <span style={s.jobName}>{j.filename || `ch${j.channel} ${fmtClock(j.start)}`}</span>
                  <JobStatus j={j} onSave={() => saveFile(j)} onView={() => playJob(j)} onCancel={() => cancel(j.id)} />
                </div>
                <div style={s.jobMeta}>Cam {j.channel} · {fmtClock(j.start)}→{fmtClock(j.end)} · {fmtBytes(j.size)}</div>
                {(j.status === 'running' || j.status === 'queued') && (
                  <>
                    <div style={s.bar}><div style={{ ...s.barFill, width: `${j.percent || 0}%` }} /></div>
                    <div style={s.jobStats}>
                      {j.status === 'queued' ? 'en cola…'
                        : j.kind === 'chunked'
                          ? `tramo ${j.chunks_done}/${j.chunks_total} · ${j.percent || 0}% (resiliente)`
                          : `${j.percent || 0}% · ${(j.speed_bps / 1024 / 1024).toFixed(1)} MB/s · faltan ${fmtDur(j.eta_seconds)}`}
                    </div>
                  </>
                )}
                {j.status === 'waiting' && (
                  <div style={s.waitMsg}>
                    <RefreshCw size={13} className="spin" />{' '}
                    {j.error || `Esperando que el equipo vuelva… se reanuda solo${j.attempts > 1 ? ` (intento ${j.attempts})` : ''}`}
                  </div>
                )}
                {(j.status === 'error' || (j.status === 'done' && j.error)) && (
                  <div style={s.jobErr}>{j.error}</div>
                )}
              </div>
            ))}
            {!queue.jobs?.length && <div style={s.muted}>Nada en la cola. Encola segmentos desde la izquierda.</div>}
          </div>
        </div>
      </div>

      {player && <PlayerModal player={player} onClose={() => setPlayer(null)} onClip={enqueueClip} />}
    </div>
  );
};

const JobStatus = ({ j, onSave, onView, onCancel }) => {
  if (j.status === 'done') return (
    <div style={{ display: 'flex', gap: 5 }}>
      <button style={s.viewBtn} onClick={onView} title="Reproducir"><Play size={13} /></button>
      <button style={s.saveBtn} onClick={onSave}><Download size={14} /> Guardar</button>
    </div>
  );
  if (j.status === 'error') return <span style={s.badgeErr}><XCircle size={13} /> error</span>;
  if (j.status === 'canceled') return <span style={s.badgeMuted}>cancelada</span>;
  if (j.status === 'waiting') return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      <span style={s.badgeWait}><RefreshCw size={11} className="spin" /> esperando</span>
      <button style={s.cancelBtn} onClick={onCancel}><X size={14} /></button>
    </div>
  );
  return <button style={s.cancelBtn} onClick={onCancel}><X size={14} /></button>;
};

// Reproductor + recorte
const PlayerModal = ({ player, onClose, onClip }) => {
  const vref = React.useRef(null);
  const [dur, setDur] = React.useState(0);
  const [inS, setInS] = React.useState(0);
  const [outS, setOutS] = React.useState(0);
  const seg = player.seg;

  const setInHere = () => { if (vref.current) setInS(Math.floor(vref.current.currentTime)); };
  const setOutHere = () => { if (vref.current) setOutS(Math.ceil(vref.current.currentTime)); };

  return (
    <div style={s.modal} onClick={onClose}>
      <div style={s.modalInner} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHead}>
          <span>{player.title}</span>
          <button style={s.closeBtn} onClick={onClose}><X size={18} /></button>
        </div>
        <div style={s.modalVideo}>
          <video ref={vref} src={player.src} controls autoPlay style={{ width: '100%', height: '100%', background: '#000' }}
                 onLoadedMetadata={(e) => { const d = e.target.duration; if (isFinite(d)) { setDur(d); setOutS(Math.min(30, Math.floor(d) || 30)); } }} />
        </div>
        {player.isup && (
          <div style={s.isupNote}>
            <Globe size={13} /> Equipo remoto: reproduciendo por túnel ISUP. Arranca en unos
            segundos y el MDVR lo empuja comprimido, así que puede verse con bloques en las
            zonas con detalle. Cuando el equipo esté en LAN/VPN, saldrá 1080p limpio automáticamente.
          </div>
        )}
        {seg && (
          <div style={s.trimBar}>
            <div style={s.trimRow}>
              <Scissors size={16} color="#7c3aed" />
              <span style={s.trimLabel}>Recorte:</span>
              <button style={s.markBtn} onClick={setInHere}>Marcar inicio</button>
              <span style={s.trimVal}>{mmss(inS)}</span>
              <span style={s.trimSep}>→</span>
              <button style={s.markBtn} onClick={setOutHere}>Marcar fin</button>
              <span style={s.trimVal}>{mmss(outS)}</span>
              <span style={s.trimDur}>({mmss(Math.max(0, outS - inS))})</span>
            </div>
            <button style={s.clipBtn} disabled={outS <= inS}
                    onClick={() => { onClip(seg, inS, outS); onClose(); }}>
              <Scissors size={15} /> Encolar recorte {mmss(Math.max(0, outS - inS))}
            </button>
            <div style={s.trimHint}>
              Ve el video, marca inicio y fin, y encola el recorte. Sale un MP4 exacto (solo video).
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CSS = `
@keyframes barMove{0%{background-position:0 0}100%{background-position:40px 0}}
@keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.rec button{transition:transform .12s ease, box-shadow .12s ease, filter .12s ease}
.rec button:hover{filter:brightness(1.03)}
.rec [data-act]:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(37,99,235,.18)}
.rec [data-act]:active{transform:translateY(0)}
.rec [data-row]:hover{border-color:#bfdbfe;box-shadow:0 2px 10px rgba(15,23,42,.06)}
.rec [data-row]{transition:border-color .12s ease, box-shadow .12s ease, background .12s ease}
.rec ::-webkit-scrollbar{width:8px;height:8px}
.rec ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:8px}
.rec ::-webkit-scrollbar-thumb:hover{background:#94a3b8}
`;
const RING = '1px solid #e5e7eb';
const s = {
  page: { padding: 22, maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  title: { display: 'flex', alignItems: 'center', gap: 12 },
  titleIcon: { width: 40, height: 40, borderRadius: 11, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' },
  h1: { fontSize: 19, fontWeight: 700, color: '#0f172a' },
  h1sub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  netPill: { padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: 'monospace' },
  netOn: { background: '#dcfce7', color: '#15803d' },
  netOff: { background: '#fee2e2', color: '#b91c1c' },
  modePill: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 },
  modeClean: { background: '#dcfce7', color: '#15803d' },
  modeIsup: { background: '#fef3c7', color: '#b45309' },
  controls: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 },
  select: { padding: '9px 12px', borderRadius: 9, border: RING, background: '#fff', fontSize: 14, fontWeight: 600, minWidth: 180 },
  chips: { display: 'flex', gap: 4, background: '#eef2f7', padding: 4, borderRadius: 10 },
  chip: { border: 'none', background: 'transparent', padding: '6px 12px', borderRadius: 7, cursor: 'pointer', color: '#475569', fontWeight: 600, fontSize: 13 },
  chipA: { border: 'none', background: '#3b82f6', color: '#fff', padding: '6px 12px', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  date: { padding: '8px 12px', borderRadius: 9, border: RING, fontSize: 14 },
  reload: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 9, border: RING, background: '#fff', cursor: 'pointer', color: '#334155', fontWeight: 600, fontSize: 13 },
  summary: { display: 'flex', gap: 26, flexWrap: 'wrap', padding: '14px 18px', background: '#f8fafc', border: RING, borderRadius: 12, marginBottom: 16 },
  sumItem: { display: 'flex', gap: 10, alignItems: 'center' },
  sumLabel: { fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 },
  sumVal: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
  sumEmpty: { color: '#94a3b8' },
  body: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  col: { background: '#fff', border: RING, borderRadius: 14, padding: 14, minHeight: 260 },
  colHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: '#0f172a', marginBottom: 10, fontSize: 14 },
  linkBtn: { border: 'none', background: 'transparent', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  avisoDatos: { display: 'flex', alignItems: 'center', gap: 8, background: '#fef3c7',
                color: '#92400e', border: '1px solid #fde68a', borderRadius: 10,
                padding: '9px 12px', fontSize: 13, marginTop: 10 },
  err: { display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c', background: '#fef2f2', padding: 10, borderRadius: 9, fontSize: 13, marginBottom: 8 },
  muted: { color: '#94a3b8', fontSize: 13, padding: 12, textAlign: 'center' },
  segList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 380, overflowY: 'auto' },
  segRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9, border: RING, cursor: 'pointer', background: '#fff' },
  segRowA: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9, border: '1px solid #bfdbfe', cursor: 'pointer', background: '#eff6ff' },
  segTime: { fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', fontSize: 14 },
  segMeta: { fontSize: 12, color: '#64748b', marginTop: 1 },
  dlBtn: { border: RING, background: '#fff', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#3b82f6' },
  playBtn: { border: RING, background: '#eff6ff', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#7c3aed' },
  chunkBtn: { border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#16a34a' },
  isupDlBtn: { border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#b45309' },
  isupNote: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: 12, color: '#b45309', background: '#fffbeb', borderTop: '1px solid #fde68a' },
  viewBtn: { border: RING, background: '#fff', borderRadius: 8, width: 30, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#7c3aed' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(2,6,23,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modalInner: { width: '82vw', maxWidth: 1000, background: '#0f172a', borderRadius: 14, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,.5)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', color: '#e5e7eb', fontSize: 14, fontWeight: 600, fontFamily: 'monospace' },
  modalVideo: { aspectRatio: '16 / 9', background: '#000' },
  closeBtn: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex' },
  trimBar: { padding: 14, background: '#0b1120' },
  trimRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', color: '#cbd5e1', fontSize: 13, marginBottom: 10 },
  trimLabel: { fontWeight: 600 },
  markBtn: { border: '1px solid #334155', background: '#1e293b', color: '#e5e7eb', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  trimVal: { fontFamily: 'monospace', fontWeight: 700, color: '#fff', minWidth: 42, textAlign: 'center' },
  trimSep: { color: '#64748b' },
  trimDur: { color: '#a78bfa', fontWeight: 700 },
  clipBtn: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#7c3aed,#6366f1)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  trimHint: { fontSize: 11, color: '#64748b', marginTop: 8, textAlign: 'center' },
  enqueueBtn: { marginTop: 12, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  keepCard: { display: 'flex', gap: 12, alignItems: 'center', padding: 14, borderRadius: 12, background: '#fffbeb', border: '1px solid #fde68a', marginBottom: 12 },
  keepTitle: { fontWeight: 800, color: '#b45309', fontSize: 15 },
  keepSub: { fontSize: 12, color: '#92400e', marginTop: 2 },
  jobList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' },
  job: { border: RING, borderRadius: 11, padding: 11 },
  jobTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  jobName: { fontWeight: 600, color: '#0f172a', fontSize: 13, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  jobMeta: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  bar: { height: 7, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden', marginTop: 8 },
  barFill: { height: '100%', background: 'repeating-linear-gradient(45deg,#3b82f6,#3b82f6 10px,#60a5fa 10px,#60a5fa 20px)', backgroundSize: '40px 40px', animation: 'barMove 1s linear infinite', transition: 'width .4s' },
  jobStats: { fontSize: 12, color: '#475569', marginTop: 5 },
  jobErr: { fontSize: 12, color: '#b91c1c', marginTop: 6 },
  saveBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, border: 'none', background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 12 },
  cancelBtn: { border: RING, background: '#fff', borderRadius: 8, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' },
  badgeErr: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b91c1c', fontSize: 12, fontWeight: 600 },
  badgeWait: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 },
  badgeMuted: { color: '#94a3b8', fontSize: 12 },
  waitMsg: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#b45309', marginTop: 6 },
};

const CSS2 = `@keyframes spin{to{transform:rotate(360deg)}} .spin{animation:spin 1s linear infinite}`;
if (typeof document !== 'undefined' && !document.getElementById('rec-spin')) {
  const st = document.createElement('style'); st.id = 'rec-spin'; st.textContent = CSS2;
  document.head.appendChild(st);
}

export default Recordings;
